import { useState, useEffect, useRef } from "react";

type DayAvailability = "available" | "partial" | "unavailable";

interface UseStaffCalendarAvailabilityReturn {
  getDateAvailability: (date: Date) => DayAvailability | undefined;
  isLoading: boolean;
  error: string | null;
}

export const useStaffCalendarAvailability = (
  venueId: string | null,
  customerTypeId: string | null,
  participants?: number | null,
  timeSlotStart?: string | null,
  timeSlotEnd?: string | null,
): UseStaffCalendarAvailabilityReturn => {
  const [availabilityData, setAvailabilityData] = useState<
    Record<string, DayAvailability>
  >({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const toLocalDateString = (d: Date) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    if (!venueId || !customerTypeId) {
      setAvailabilityData({});
      setIsLoading(false);
      setError(null);
      return;
    }

    // Clear any pending debounced fetch
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const fetchAvailability = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Calculate date range to include more months ahead
        const today = new Date();
        const endDate = new Date(today);
        // Fetch 365 days (1 year) of availability data
        endDate.setDate(today.getDate() + 365);

        const startDateStr = toLocalDateString(today);
        const endDateStr = toLocalDateString(endDate);

        const params = new URLSearchParams({
          venue_id: venueId,
          customer_type_id: customerTypeId,
          start_date: startDateStr,
          end_date: endDateStr,
          ...(participants && { participants: participants.toString() }),
          ...(timeSlotStart && { timeSlotStart }),
          ...(timeSlotEnd && { timeSlotEnd }),
        });

        const response = await fetch(
          `/api/staff/venues/${venueId}/calendar-dates?${params.toString()}`,
        );
        const data = await response.json();

        if (data.success) {
          // Convert API response to simple date->status map
          const availabilityMap: Record<string, DayAvailability> = {};
          data.data.forEach((dayData: any) => {
            availabilityMap[dayData.date] = dayData.status;
          });

          setAvailabilityData(availabilityMap);
        } else {
          console.error("❌ Staff calendar-dates API error:", data.error);
          setError(data.error || "Failed to load staff calendar availability");
        }
      } catch (err) {
        console.error("❌ Staff availability fetch error:", err);
        setError("Failed to load staff calendar availability");
      } finally {
        setIsLoading(false);
      }
    };

    // Debounce the fetch by 300ms to avoid multiple rapid fetches
    // when multiple parameters change at the same time
    debounceTimerRef.current = setTimeout(fetchAvailability, 300);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [venueId, customerTypeId, participants, timeSlotStart, timeSlotEnd]);

  const getDateAvailability = (date: Date): DayAvailability | undefined => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const dateStr = `${year}-${month}-${day}`;
    return availabilityData[dateStr];
  };

  return {
    getDateAvailability,
    isLoading,
    error,
  };
};
